import cookies from 'js-cookie';
import qs from 'qs';
import { BLAdmin } from './types';
import { isApiUrlSameOrigin, isLocalhost, jwtDecode } from './utils';
import { BLApi } from './bl-api';
import { BLApiError, BLAuthError } from './errors';
import { RedirectParams } from './types';
import { v4 as uuid } from 'uuid';
import { ALLOWED_STATES, Cookies } from './constants';
import { Storage } from './storage';
import { URLParams } from './url-params';
import { SessionWatcher } from './session-watcher';
import { ActivityTracker, ActivityTrackerOptions } from './activity-tracker';

export class BLAuth extends BLApi {
	public signedIn: boolean;
	public initialized: boolean;
	public idToken?: string;
	public refreshToken?: string;
	public accessToken?: string;
	public user?: BLAdmin;
	protected sessionWatcher: SessionWatcher;
	protected storage: Storage;
	protected _enabled: boolean;
	protected activityTracker?: ActivityTracker;


	constructor(public readonly apiHost: string, public readonly loginHost: string, public readonly enableContinuousRefresh: boolean = false) {
		// will run in CORS mode if not used at a brandlive domain
		super(!isApiUrlSameOrigin(apiHost), apiHost);
		this.sessionWatcher = new SessionWatcher();
		this.initialized = false;
		this.signedIn = false;
		this.storage = new Storage();
		this.idToken = this.storage.idToken;
		this.refreshToken = (cookies.get(Cookies.refreshToken) || this.storage.refreshToken) ?? undefined;
		this.user = this.idToken ? jwtDecode(this.idToken) as BLAdmin : undefined;
		this.checkTokens();
		this.confirmSettings();
		if (this.enableContinuousRefresh) {
			this.continuousRefresh();
		}
		this._enabled = true;
	}

	public get enabled(): boolean {
		return this._enabled;
	}

	public set enabled(value: boolean) {
		this._enabled = value;
		this.sessionWatcher.enabled = value;
	}

	private confirmSettings = (): void => {
		if (!this.loginHost) {
			console.error(`LOGIN_HOST NOT SET`);
		}

		if (!this.apiHost) {
			console.error(`API HOST NOT SET`);
		}
	};

	private checkTokens = () => {
		if (this.refreshToken) {
			const contents = jwtDecode(this.refreshToken) as { exp: number };
			if (contents.exp * 1000 < Date.now()) {
				this.refreshToken = undefined;
				this.storage.refreshToken = undefined;
				this.sessionWatcher.signedOut();
			}
		}

		if (this.accessToken) {
			const contents = jwtDecode(this.accessToken) as { exp: number };
			if (contents.exp * 1000 < Date.now()) {
				this.accessToken = undefined;
				this.storage.accessToken = undefined;
			}
		}

		if (this.idToken) {
			const contents = jwtDecode(this.idToken) as { exp: number };
			if (contents.exp * 1000 < Date.now()) {
				this.idToken = undefined;
				this.user = undefined;
				this.storage.idToken = undefined;
			}
		}
	};

	public handleRedirected = async (): Promise<void> => {
		const params = new URLParams();

		if (params.isFromLogin()) {
			// user arrived with the proper tokens - should reset the redirect count
			this.storage.resetRedirected();
			const valid = this.validState(params.state as string);

			if (valid) {
				await this.signInWithCode(params.code as string);
				this.storage.authState = undefined;
				window.history.replaceState({}, document.title, window.location.pathname);
			}
		}
	};

	public async resume(): Promise<void> {
		try {
			// debugger;
			if (this.idToken && this.refreshToken && ((this.corsMode && this.accessToken) || !this.corsMode)) {
				this.user = jwtDecode(this.idToken) as BLAdmin;
				this.signedIn = true;
				this.dispatchEvent(new Event('signedIn'));

				// user is signed in, reset the redirect count
				this.storage.resetRedirected();
			} else if (this.refreshToken) {
				await this.refresh();
			}

			this.initialized = true;
		} catch (e) {
			console.error('Error initializing BLAuth:', e);
		}
	}

	public signOut = async (): Promise<void> => {
		this.storage.reset();
		await this.put({
			url: '/v4/admin/access-token/logout',
			token: this.accessToken
		}).catch(e => {
			console.error('Error logging out:', e);
		});

		this.idToken = undefined;
		this.refreshToken = undefined;
		this.accessToken = undefined;
		this.user = undefined;
		this.signedIn = false;
		this.dispatchEvent(new Event('signedOut'));
		this.sessionWatcher.signedOut();
	}

	shouldSignIn = async (): Promise<boolean> => {
		try {
			if (this.corsMode) {
				return !this.refreshToken;
			}

			await this.get({ url: '/v4/admin/access-token/check' });
			return false;
		} catch (e) {
			if (e instanceof BLApiError) {
				return true;
			}

			throw e;
		}
	};

	private continuousRefresh = async ()=> {
		setInterval(this.refresh, 1000 * 60 * 5);
	};

	refresh = async (): Promise<void> => {
		try {
			if (!this.refreshToken) {
				this.dispatchEvent(new Event("noRefreshToken"));
				return;
			}

			let endpoint = '/authenticate/refresh-token';

			if(isLocalhost(this.apiHost)) {
				endpoint = endpoint + `?refresh_token=${this.refreshToken}`;
			}

			this.dispatchEvent(new Event("refreshing"));
			const {
				id_token,
				access_token
			} = await this.get<{
				id_token: string,
				access_token: string
			}>({ url: endpoint }); // Refresh token should be included in the headers on non localhost

			if (id_token) {
				// user has a valid id token, reset the redirect count
				this.storage.resetRedirected();

				this.idToken = id_token;
				this.user = jwtDecode(this.idToken) as BLAdmin;
				this.signedIn = true;
				this.accessToken = access_token;
				this.storage.idToken = this.idToken;

				if (this.corsMode && this.accessToken) {
					this.storage.accessToken = this.accessToken;
				}

				this.dispatchEvent(new Event('signedIn'));
			}
		} catch (e) {
			if (e instanceof BLApiError) {
				const redirectStatuses = [400, 401, 403, 404];
				if (redirectStatuses.includes(e.status)) {
					await this.signOut();
					console.warn(`Token refresh invalid: ${e.message}`);
					this.redirectToSignIn();
					return;
				}
			}
			
			console.error('Error refreshing token:', e);
			throw e;
		}
	};

	signInWithCode = async (code: string): Promise<void> => {
		const {
			id_token,
			refresh_token,
			access_token
		} = await this.get<{
			id_token: string,
			refresh_token: string,
			access_token?: string
		}>({ url: `/authenticate/access-token?code=${code}` });
		
		if (id_token) {
			// user has a valid id token, reset the redirect count
			this.storage.resetRedirected();

			this.idToken = id_token;
			this.refreshToken = refresh_token || cookies.get(Cookies.refreshToken);
			this.user = jwtDecode(this.idToken) as BLAdmin;
			this.signedIn = true;
			this.accessToken = access_token;
			this.storage.idToken = this.idToken;
		
			if (this.corsMode && this.refreshToken) {
				this.storage.refreshToken = this.refreshToken;
				
				if (this.accessToken) {
					this.storage.accessToken = this.accessToken;
				}
			}
			
			this.sessionWatcher.signedIn();
			this.dispatchEvent(new Event('signedIn'));
		}
	};

	validState = (state: string): boolean => {
		const storedState = this.storage.authState;
		return storedState === state || ALLOWED_STATES.includes(state);
	};

	redirectToSignIn = (redirectParams: RedirectParams = {}): void => {
		if (this.storage.redirectCount >= 3) {
			this.storage.resetRedirected();
			throw new BLAuthError('Too many redirects');
		}

		redirectParams.state = redirectParams.state || uuid();
		const params = qs.stringify(redirectParams);
		this.storage.authState = redirectParams.state;
		this.storage.incrRedirected();
		window.location.href = `${this.loginHost}/sign-in?${params}`;
	};

	handleAuthRedirect = async (): Promise<{ success: boolean, error?: string }> => {
		const params = new URLParams();

		if (params.code && params.state) {
			this.storage.resetRedirected();

			if (!this.validState(params.state)) {
				return {
					success: false,
					error: 'Invalid state'
				};
			}
		
			try {
				await this.signInWithCode(params.code);
				this.storage.authState = undefined;
				return { success: true }
			} catch (e) {
				if (e instanceof BLApiError) {
					return {
						success: false,
						error: e.message
					}
				}

				console.error(e);
				return {
					success: false,
					error: 'Unknown error'
				}
			}
		}

		return {
			success: false,
			error: 'No code or state'
		}
	};

	canAccessChannel = (channelId: string): boolean => {
		if (!this.user) return false;
		return !!this.user.channels?.[channelId];
	};

	public initializeActivityTracker = (options: ActivityTrackerOptions = {}): void => {
		if(this.activityTracker) {
			return;
		}
		this.activityTracker = new ActivityTracker(options, this.signOut);
	}
}
